import { useEffect, useMemo } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { MdTranslate } from 'react-icons/md';
import { useActiveLocales } from '@features/locales/hooks';
import type { Locale } from '@features/locales/schema';
import {
  tagFormSchema,
  type Tag,
  type TagDetail,
  type TagFormTranslation,
  type TagFormValues,
} from '../schema';
import { useCreateTag, useTag, useUpdateTag } from '../hooks';

interface Props {
  open: boolean;
  tag: Tag | null;
  onClose: () => void;
}

function buildFormTranslations(
  locales: Locale[],
  existing: TagDetail['translations'] | undefined,
): TagFormTranslation[] {
  const byCode = new Map(existing?.map((t) => [t.localeCode, t]) ?? []);
  return locales
    .filter((l) => !l.isDefault)
    .map((locale) => ({
      localeCode: locale.code,
      localeName: locale.name,
      name: byCode.get(locale.code)?.name ?? '',
    }));
}

function buildEmptyDefaults(
  locales: Locale[],
  existing?: TagDetail | null,
): TagFormValues {
  return {
    code: existing?.code ?? '',
    name: existing?.name ?? '',
    isActive: existing?.isActive ?? true,
    translations: buildFormTranslations(locales, existing?.translations),
  };
}

export function TagFormDialog({ open, tag, onClose }: Props) {
  const isEditing = !!tag;
  const createMutation = useCreateTag();
  const updateMutation = useUpdateTag();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const { data: locales = [], isLoading: localesLoading } = useActiveLocales();
  const detailQuery = useTag(tag?.id ?? null);
  const detail = detailQuery.data ?? null;

  const dataReady = !localesLoading && (!isEditing || detailQuery.isSuccess);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TagFormValues>({
    resolver: zodResolver(tagFormSchema) as Resolver<TagFormValues>,
    defaultValues: useMemo(() => buildEmptyDefaults([]), []),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!open || !dataReady) return;
    reset(buildEmptyDefaults(locales, isEditing ? detail : null));
  }, [open, dataReady, locales, detail, isEditing, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (isEditing && tag) {
      await updateMutation.mutateAsync({ id: tag.id, values });
    } else {
      await createMutation.mutateAsync(values);
    }
    onClose();
  });

  const translations = watch('translations');

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit tag' : 'New tag'}</DialogTitle>
      <DialogContent dividers>
        {!dataReady ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <form id="tag-form" onSubmit={onSubmit} className="grid gap-4 pt-2">
            <TextField
              label="Code"
              size="small"
              fullWidth
              disabled={isEditing}
              error={!!errors.code}
              helperText={
                errors.code?.message ??
                (isEditing
                  ? 'Code is immutable post-create.'
                  : 'Lowercase alphanumerics joined by hyphens (e.g. market-update).')
              }
              {...register('code')}
            />

            <TextField
              label="Name"
              size="small"
              fullWidth
              error={!!errors.name}
              helperText={errors.name?.message ?? 'Base (default-locale) display name.'}
              {...register('name')}
            />

            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[13px] text-foreground">
                      {field.value
                        ? 'Active — appears in the news-form picker'
                        : 'Inactive — hidden from the picker; existing attachments preserved'}
                    </span>
                  }
                />
              )}
            />

            {translations.length > 0 && (
              <div className="grid gap-3">
                <div className="-mb-1 mt-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground-muted">
                  <MdTranslate size={14} />
                  Translations <span className="font-normal normal-case">· optional</span>
                </div>
                {translations.map((t, i) => {
                  const nameErr = errors.translations?.[i]?.name?.message;
                  return (
                    <TextField
                      key={t.localeCode}
                      label={`Name (${t.localeName})`}
                      size="small"
                      fullWidth
                      error={!!nameErr}
                      helperText={nameErr ?? ' '}
                      {...register(`translations.${i}.name` as const)}
                    />
                  );
                })}
              </div>
            )}
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="tag-form"
          variant="contained"
          disabled={submitting || !dataReady}
        >
          {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
